/**
 * Tags Admin API
 *
 * Admin-only endpoints for tag management and reporting: fetch/update tags on
 * templates, counts, and categorized statistics. Directly uses `fetch` for
 * a few endpoints to combine with Firebase ID token.
 */
import { auth } from '../../firebase/firebase';

import { SERVER_URL } from '../core';

// Get templates for tag adjustment (admin only)
export const getTemplatesForTagAdjustment = async (page = 1, limit = 25, type = null, searchType = 'newlyAdded') => {
  try {
    const idToken = await auth.currentUser.getIdToken();
    
    // Build URL with optional type parameter
    let url = `${SERVER_URL}/admin/tags/templates?page=${page}&limit=${limit}&searchType=${searchType}`;
    if (type) {
      url += `&type=${type}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching templates for tag adjustment:', error);
    throw error;
  }
};

// Update tags for a specific template (admin only)
export const updateTemplateTags = async (templateId, tags) => {
  try {
    const idToken = await auth.currentUser.getIdToken();
    
    const response = await fetch(`${SERVER_URL}/admin/tags/templates/${templateId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tags }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating template tags:', error);
    throw error;
  }
};

// Get a specific template for tag adjustment (admin only)
export const getTemplateForTagAdjustment = async (templateId) => {
  try {
    const idToken = await auth.currentUser.getIdToken();
    
    const response = await fetch(`${SERVER_URL}/admin/tags/templates/${templateId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching template for tag adjustment:', error);
    throw error;
  }
};

// Get tag counts for admin dashboard
export const getTagCounts = async () => {
  try {
    const response = await fetch(`${SERVER_URL}/admin/tags/counts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || errorData.error || 'Failed to fetch tag counts');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching tag counts:', error);
    throw error;
  }
};

// Get categorized tag usage statistics (admin only)
export const getCategorizedTagStats = async () => {
  try {
    const response = await fetch(`${SERVER_URL}/admin/tags/comprehensive-stats-by-category`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || errorData.error || 'Failed to fetch categorized tag statistics');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching categorized tag statistics:', error);
    throw error;
  }
};

// Default export object
const tagsApi = {
  getTemplatesForTagAdjustment,
  updateTemplateTags,
  getTemplateForTagAdjustment,
  getTagCounts,
  getCategorizedTagStats
};

export default tagsApi; 